var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import theme from '@adsk/alloy-react-theme';
import { useMeasure } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import { getNodeAncestors } from '../helpers/treeHelpers';
import { shouldNameBeTruncated } from '../helpers/locationNodeHelpers';
import { useLocations } from '../LocationProvider/LocationProvider';
import { usePrevious } from '../LocationPicker/hooks';
import { useUnspecifiedProps } from '../helpers/unspecifiedHelper';
export var TruncationLevels;
(function (TruncationLevels) {
    TruncationLevels[TruncationLevels["NONE"] = 0] = "NONE";
    TruncationLevels[TruncationLevels["MID_LEVEL"] = 1] = "MID_LEVEL";
    TruncationLevels[TruncationLevels["ALL_EXCEPT_LAST"] = 2] = "ALL_EXCEPT_LAST";
})(TruncationLevels || (TruncationLevels = {}));
var TruncationLevelsValues = Object.values(TruncationLevels).filter(function (x) { return typeof x === 'string'; });
var LocationNameDisplayContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  color: inherit;\n  width: 100%;\n  ", ";\n  overflow: hidden;\n  white-space: nowrap;\n  text-overflow: ellipsis;\n  ", ";\n"], ["\n  color: inherit;\n  width: 100%;\n  ", ";\n  overflow: hidden;\n  white-space: nowrap;\n  text-overflow: ellipsis;\n  ", ";\n"])), theme.typography.bodyMedium, function (props) { return (__assign({}, props.style)); });
export var LocationNameDisplay = function (_a) {
    var locationNodeId = _a.locationNodeId, onLocationNameDisplayCalcDone = _a.onLocationNameDisplayCalcDone, notFoundText = _a.notFoundText, _b = _a.style, style = _b === void 0 ? {} : _b, className = _a.className;
    var T = useLocale(function (_a) {
        var locale = _a.locale;
        return require("./i18n/".concat(locale, ".json"));
    }).T;
    var _c = useLocations(), treeDataMap = _c.treeDataMap, isLoading = _c.isLoading;
    var _d = useUnspecifiedProps(), unspecifiedId = _d.unspecifiedId, unspecifiedItem = _d.unspecifiedItem;
    treeDataMap[unspecifiedId] = unspecifiedItem;
    var _e = __read(useState(), 2), locationNode = _e[0], setLocationNode = _e[1];
    var prevLocationNode = usePrevious(locationNode);
    var _f = __read(useState(''), 2), displayNameForCalc = _f[0], setDisplayNameForCalc = _f[1];
    var _g = __read(useState(''), 2), displayName = _g[0], setDisplayName = _g[1];
    var prevDisplayName = usePrevious(displayName);
    var _h = __read(useState(null), 2), currentTruncationLevel = _h[0], setCurrentTruncationLevel = _h[1];
    var locationNameRef = useRef(null);
    var locationNameMeasures = useMeasure(locationNameRef);
    var prevLocationNameMeasures = usePrevious(locationNameMeasures);
    var truncationMethods = useMemo(function () {
        var _a;
        return (_a = {},
            _a[TruncationLevels.NONE] = function () {
                var locationNode = treeDataMap[locationNodeId];
                var ancestorsNames = getNodeAncestors(treeDataMap, locationNode).map(function (node) { return node.name; });
                var calcedDisplayName = ancestorsNames
                    .slice(1)
                    .concat([locationNode.name])
                    .join(' > ');
                setDisplayNameForCalc(calcedDisplayName);
                setCurrentTruncationLevel(TruncationLevels.NONE);
            },
            _a[TruncationLevels.MID_LEVEL] = function () {
                var _a;
                var locationNode = treeDataMap[locationNodeId];
                var calcedDisplayName = locationNode.name;
                var ancestors = getNodeAncestors(treeDataMap, locationNode);
                var firstAncestorName = (_a = ancestors[1]) === null || _a === void 0 ? void 0 : _a.name;
                if (firstAncestorName) {
                    var namePathDecorator = ancestors.length > 2 ? '... > ' : '';
                    calcedDisplayName = "".concat(firstAncestorName, " > ").concat(namePathDecorator).concat(calcedDisplayName);
                }
                setDisplayNameForCalc(calcedDisplayName);
                setCurrentTruncationLevel(TruncationLevels.MID_LEVEL);
            },
            _a[TruncationLevels.ALL_EXCEPT_LAST] = function () {
                var locationNode = treeDataMap[locationNodeId];
                setDisplayNameForCalc("... > ".concat(locationNode.name));
                setCurrentTruncationLevel(TruncationLevels.ALL_EXCEPT_LAST);
            },
            _a);
    }, [treeDataMap, locationNodeId]);
    useEffect(function () {
        setLocationNode(treeDataMap[locationNodeId]);
    }, [locationNodeId, treeDataMap]);
    useEffect(function () {
        if (prevLocationNode !== undefined &&
            prevLocationNameMeasures.width !== undefined &&
            (locationNode !== prevLocationNode ||
                locationNameMeasures.width > prevLocationNameMeasures.width)) {
            setCurrentTruncationLevel(null);
        }
    }, [locationNameMeasures, prevLocationNameMeasures, locationNode]);
    useEffect(function () {
        var _a;
        if (locationNameRef.current && locationNodeId && !isLoading) {
            if (!treeDataMap[locationNodeId]) {
                var localizedNotFoundText = notFoundText === undefined
                    ? T.translate('notFoundText')
                    : notFoundText;
                setDisplayNameForCalc(localizedNotFoundText);
                setDisplayName(localizedNotFoundText);
                return;
            }
            if (currentTruncationLevel === null) {
                (_a = truncationMethods[TruncationLevels.NONE]) === null || _a === void 0 ? void 0 : _a.call(truncationMethods);
            }
            else if (shouldNameBeTruncated(locationNameRef.current) &&
                currentTruncationLevel < TruncationLevelsValues.length - 1) {
                truncationMethods[currentTruncationLevel + 1]();
            }
            else {
                setDisplayName(displayNameForCalc);
            }
        }
    }, [
        currentTruncationLevel,
        isLoading,
        locationNodeId,
        locationNameMeasures,
        notFoundText,
    ]);
    useEffect(function () {
        if (onLocationNameDisplayCalcDone) {
            if (prevDisplayName !== undefined && displayName !== prevDisplayName) {
                onLocationNameDisplayCalcDone(displayName);
            }
        }
    }, [displayName, prevDisplayName, onLocationNameDisplayCalcDone]);
    return (React.createElement(LocationNameDisplayContainer, { className: className, ref: locationNameRef, style: style }, displayNameForCalc));
};
var templateObject_1;
